import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { ArrowLeft, ArrowSquareOut, FileText } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { BuildingDTO } from "shared/types/building";
import { YallyCompany } from "shared/types/yally-company";
import { UnitDTO } from "shared/types/unit";
import ButtonNew from "components/buttonNew/buttonNew";
import PdfPreview from "shared/components/uploadFileModal/pdfPreview";
import AddContractForm from "./modalAddContract";
import { PreviewDocument } from "./contractExtraction/contractExtractionApi";

/**
 * Full-page split view for creating a rental contract: the contract form on the
 * left, a live PDF preview of the analysed/selected Huurcontract on the right —
 * so the user can verify Ella's extraction against the source document.
 *
 * Replaces the old add-contract modal. Building/company/units + any prefilled
 * data come via router `location.state`; on hard refresh we fetch the building.
 */
const ContractCreatePage: React.FC = () => {
  const { id: buildingId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useSession();

  const stateData = (location.state || {}) as {
    company?: YallyCompany;
    units?: UnitDTO[];
    building?: BuildingDTO;
    prefilledContacts?: any[];
    prefilledUnitId?: string;
    prefilledContractType?: string;
    prefilledNumResidents?: number;
    attributeOnlyDefault?: boolean;
    prefilledBuildingAttributeId?: number;
  };

  const [building, setBuilding] = useState<BuildingDTO | undefined>(stateData.building);
  const [previewDoc, setPreviewDoc] = useState<PreviewDocument | null>(null);

  // Hard refresh / deep link: state is gone, fetch the building to recover company + units.
  useEffect(() => {
    if (building || !buildingId) return;
    (async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/building/${buildingId}?contracts=false`,
          { method: "GET" },
          session,
        );
        if (response.ok) setBuilding(await response.json());
      } catch (error) {
        console.error("Error fetching building for contract page:", error);
      }
    })();
  }, [building, buildingId, session]);

  const goBack = () => navigate(`/building/${buildingId}/verhuur`);

  const company = stateData.company || building?.company;
  const units = (stateData.units || building?.units || []) as UnitDTO[];

  return (
    <div className="flex h-screen flex-col bg-neutral-secondary-fine/30">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-neutral-primary/10 bg-white px-6 py-3">
        <ButtonNew
          variant="secondaryNeutralMid"
          icon={<ArrowLeft />}
          label="Terug"
          onClick={goBack}
        />
        <h1 className="text-xl font-semibold text-neutral-primary">Nieuw contract</h1>
      </header>

      {/* Split view */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Left: contract form */}
        <div className="flex min-h-0 flex-col overflow-hidden border-r border-neutral-primary/10 bg-neutral-secondary-fine/40 p-6 lg:w-[55%]">
          {company ? (
            <AddContractForm
              company={company}
              units={units}
              building={building}
              onClose={goBack}
              onPreviewDocument={setPreviewDoc}
              prefilledContacts={stateData.prefilledContacts}
              prefilledUnitId={stateData.prefilledUnitId}
              prefilledContractType={stateData.prefilledContractType}
              prefilledNumResidents={stateData.prefilledNumResidents}
              attributeOnlyDefault={stateData.attributeOnlyDefault}
              prefilledBuildingAttributeId={stateData.prefilledBuildingAttributeId}
            />
          ) : (
            <p className="text-sm text-neutral-secondary-mid">Gebouwgegevens laden…</p>
          )}
        </div>

        {/* Right: PDF preview of the analysed/selected contract */}
        <div className="flex min-h-0 flex-col overflow-y-auto bg-neutral-secondary-fine/40 p-6 lg:w-[45%]">
          {previewDoc?.sharePointFileId ? (
            <>
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-neutral-primary">
                  {previewDoc.name || "Huurcontract"}
                </span>
                {previewDoc.sharePointFileUrl && (
                  <ButtonNew
                    variant="secondaryNeutralMid"
                    icon={<ArrowSquareOut />}
                    label="Open in nieuw tabblad"
                    onClick={() => window.open(previewDoc.sharePointFileUrl, "_blank")}
                  />
                )}
              </div>
              <PdfPreview fileLog={previewDoc} height="calc(100vh - 220px)" />
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-center text-neutral-secondary-mid">
              <FileText size={48} className="mb-3 opacity-40" />
              <p className="max-w-xs text-sm">
                Upload of selecteer een huurcontract om het hier te bekijken en de
                uitgelezen gegevens te controleren.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractCreatePage;
